import MapIcon from '@mui/icons-material/Map';
import {
  Typography,
  Stack,
  InputAdornment,
  Tooltip,
  Box,
  Divider,
  IconButton,
} from '@mui/material';
import ClientForm from 'components/ClientForm/ClientForm';
import MyLocationButton from 'core/components/MyLocationButton';
import { getGeolocationLink, getGeolocationString } from 'core/utils/orderUtils';
import { isEmpty, isNil } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TextareaAutosizeElement, TextFieldElement } from 'react-hook-form-mui';
import { useBlocker } from 'react-router-dom';
import type { FC } from 'react';

const OrderForm: FC = () => {
  const formContext = useFormContext();

  const formGeolocation = useWatch({ control: formContext.control, name: 'geolocation' });

  const leaveConfirm = useConfirm();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      formContext.formState.isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      leaveConfirm({
        description: 'Форма не сохранена. Вы уверены, что хотите покинуть страницу?',
      })
        .then((v) => {
          blocker.proceed?.();
          return v;
        })
        .catch(() => {
          blocker.reset?.();
        });
    }
  }, [blocker, leaveConfirm]);

  const handleGeolocationDetermined: PositionCallback = (position) => {
    const geolocation = getGeolocationString({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    });

    formContext.setValue('geolocation', geolocation);
  };

  const isValidFormGeolocation =
    !isEmpty(formGeolocation) && isNil(formContext.formState.errors.geolocation);

  const geolocationLink = useMemo(
    () =>
      !isEmpty(formGeolocation) && isNil(formContext.formState.errors.geolocation)
        ? getGeolocationLink(formGeolocation)
        : '',
    [formGeolocation, formContext.formState.errors.geolocation]
  );

  return (
    <Box>
      <TextareaAutosizeElement
        fullWidth
        label='Примечание'
        name='remark'
        margin='normal'
      />

      <TextareaAutosizeElement
        fullWidth
        label='Предупреждение'
        name='warningComment'
        margin='normal'
      />

      <Typography component={'p'} variant='h6' mt={2}>
        Местоположение
      </Typography>
      <Stack direction={['column', 'row']} gap={[0, 2]}>
        <TextFieldElement
          fullWidth
          label='Адрес'
          name='address'
          type='text'
          margin='normal'
        />
        <TextFieldElement
          fullWidth
          label='Координаты'
          name='geolocation'
          type='text'
          margin='normal'
          helperText='Например (Широта, Долгота): 41.876090034, 21.429420990'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Tooltip
                  title={
                    isValidFormGeolocation
                      ? 'Посмотреть на карте'
                      : 'Использовать текущее местоположение'
                  }>
                  {/* Box Нужен для сохранения постоянного якоря для tooltip */}
                  <Box>
                    {isValidFormGeolocation ? (
                      <IconButton
                        aria-label='Посмотреть на карте'
                        target='_blank'
                        href={geolocationLink}
                        edge='end'>
                        <MapIcon />
                      </IconButton>
                    ) : (
                      <MyLocationButton
                        edge='end'
                        aria-label='Использовать текущее местоположение'
                        onGeolocationDetermined={handleGeolocationDetermined}
                      />
                    )}
                  </Box>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Typography variant='h5' mt={2}>
        Клиент
      </Typography>

      <Divider sx={{ mb: 2, mt: 0.5 }} />

      <ClientForm formPath='client' />
    </Box>
  );
};

export default OrderForm;
